// Clean TurnManager - FIFO architecture
import { useBattleStore } from '@/store/useBattleStore';
import { generateTurnQueue } from '@/core/utils/battle.utils';
import { BattleStatus, BattleUnit, FactionType, TurnPool } from '@/core/types/battle.types';
import { coordsToId } from '@/core/utils/hex.utils';
import { initialPathInfo } from '@/store/battleMutations';
import ManagerRegistry from '@/core/battle/ManagerRegistry';

/**
 * IMPORTANT: TurnPool should have optional deferredUnitIds field:
 * 
 * interface TurnPool {
 *   id: string;
 *   faction: FactionType;
 *   unitIds: string[];
 *   deferredUnitIds?: Set<string>; // Track deferred units to prevent cycles
 * }
 */

export class TurnManager {
    
    /**
     * End unit's turn → move to finished pools
     */
    endUnitTurn(unitId: string): void {
        const state = useBattleStore.getState();
        const unit = state.units.get(unitId);
        
        if (!unit) return;

        // 1. Remove from active queue (immutable)
        const newActive = this.removeUnitFromActive(state.turnQueue, unitId);
        
        // 2. Add to finished (merge by faction, FIFO)
        const newFinished = this.addToFinished(state.finishedPools, unitId, unit.faction);

        // 3. Apply update
        this.applyQueueUpdate(newActive, newFinished);
    }

    /**
     * Defer unit - 3 cases:
     * 1. Move to end of current pool (if has other units AND not already deferred)
     * 2. Move to next pool of same faction
     * 3. No where to defer → end turn
     */
    deferUnit(unitId: string): void {
        return this.endUnitTurn(unitId);
        const state = useBattleStore.getState();
        const unit = state.units.get(unitId);
        console.log("nextSameFactionIdx unit", unit);
        if (!unit) return;

        const currentPool = state.turnQueue[0];
        console.log("nextSameFactionIdx currentPool", currentPool);
        if (!currentPool) return;

        // Initialize deferredUnitIds if not exists
        // const deferredSet = currentPool.deferredUnitIds || new Set<string>();

        // CASE 1: Move to end of current pool (prevent cycles)
        // const otherUnits = currentPool.unitIds.filter(id => id !== unitId);
        // if (otherUnits.length > 0 && !deferredSet.has(unitId)) {
        //     const newDeferredSet = new Set(deferredSet);
        //     newDeferredSet.add(unitId);
            
        //     const newActive = [
        //         { 
        //             ...currentPool, 
        //             unitIds: [...otherUnits, unitId],
        //             deferredUnitIds: newDeferredSet
        //         },
        //         ...state.turnQueue.slice(1)
        //     ];
            
        //     useBattleStore.setState({
        //         turnQueue: newActive,
        //         selectedUnitId: otherUnits[0]
        //     });
        //     return;
        // }

        // CASE 2: Move to next pool of same faction
        const nextSameFactionIdx = state.turnQueue.findIndex(
            (p, i) => i > 0 && p.faction === unit?.faction
        );
console.log("nextSameFactionIdx 55", nextSameFactionIdx, state);
        if (nextSameFactionIdx !== -1) {
            const newActive = this.moveToNextPool(
                state.turnQueue,
                unitId,
                nextSameFactionIdx
            );
            
            this.applyQueueUpdate(newActive, state.finishedPools);
            return;
        }

        // CASE 3: No where to defer → end turn
        this.endUnitTurn(unitId);
    }

    /**
     * Check if unit can still act (move or use skill)
     */
    canUnitStillAct(unitId: string): boolean {
        const state = useBattleStore.getState();
        const unit = state.units.get(unitId);
        if (!unit || unit.currentStamina <= 0) return false;
        
        // Check 1: Can use any skill?
        const canUseSkill = unit.skills.some(skill => 
            unit.currentStamina >= skill.cost
        );
        if (canUseSkill) return true;
        
        // Check 2: Can move?
        const staminaCosts = Array.from(state.reachableHexesWithCost.values())
            .filter(cost => cost > 0);
        const minCost = staminaCosts.length > 0 ? Math.min(...staminaCosts) : Infinity;
        const canMove = unit.currentStamina >= minCost;
        
        return canMove;
    }

    /**
     * Handle unit death
     */
    handleUnitDeath(unitId: string): void {
        const state = useBattleStore.getState();
        const unit = state.units.get(unitId);
        if (!unit || unit.isDead) return;

        // Remove from active queue
        const newActive = this.removeUnitFromActive(state.turnQueue, unitId);
        const hexId = coordsToId(unit);
        // Apply death update (через стор method для атомарності)
        useBattleStore.getState().applyUnitDeath({
            unitId,
            hexId,
            newQueue: newActive,
        });

        const registry = ManagerRegistry.getInstance().entityRegistry;
        const hex = registry.getHex(hexId);
        hex?.vacate();

        // Check battle end
        this.checkBattleEnd();
        
        // If dead unit was selected, advance turn
        if (unitId === state.selectedUnitId) {
            this.advanceTurn(newActive, state.finishedPools);
        }
    }

    // ============================================
    // PRIVATE HELPERS - Immutable operations
    // ============================================

    /**
     * Remove unit from active queue (immutable)
     * Returns new queue with empty pools filtered out
     * Preserves deferredUnitIds for remaining units
     */
    private removeUnitFromActive(queue: TurnPool[], unitId: string): TurnPool[] {
        return queue
            .map(pool => ({
                ...pool,
                unitIds: pool.unitIds.filter(id => id !== unitId)
                // deferredUnitIds preserved - other units may still be deferred
            }))
            .filter(pool => pool.unitIds.length > 0);
    }

    /**
     * Move unit to next pool (immutable)
     * Note: deferredUnitIds automatically cleared when unit leaves pool
     */
    private moveToNextPool(
        queue: TurnPool[],
        unitId: string,
        targetIdx: number
    ): TurnPool[] {
        return queue
            .map((pool, idx) => {
                if (idx === 0) {
                    // Remove from current pool
                    // deferredUnitIds stays (other units might still be deferred)
                    return {
                        ...pool,
                        unitIds: pool.unitIds.filter(id => id !== unitId)
                    };
                }
                if (idx === targetIdx) {
                    // Add to target pool (WITHOUT deferredUnitIds - fresh start)
                    return {
                        ...pool,
                        unitIds: [...pool.unitIds, unitId]
                        // deferredUnitIds intentionally omitted - clean slate in new pool
                    };
                }
                return pool;
            })
            .filter(pool => pool.unitIds.length > 0);
    }

    /**
     * Add unit to finished pools (merge by faction, FIFO)
     */
    private addToFinished(
        finishedPools: TurnPool[],
        unitId: string,
        faction: FactionType
    ): TurnPool[] {
        // Find existing pool of same faction
        const existingIdx = finishedPools.findIndex(p => p.faction === faction);
        
        if (existingIdx !== -1) {
            // Append to existing pool (immutable)
            return finishedPools.map((pool, idx) => 
                idx === existingIdx
                    ? { ...pool, unitIds: [...pool.unitIds, unitId] }
                    : pool
            );
        }
        
        // Create new finished pool
        return [
            ...finishedPools,
            {
                id: `finished-${faction}`,
                faction,
                unitIds: [unitId]
            }
        ];
    }

    /**
     * Apply queue update and advance turn if needed
     */
    private applyQueueUpdate(
        newActive: TurnPool[],
        newFinished: TurnPool[]
    ): void {
        const state = useBattleStore.getState();
        const currentPool = newActive[0];

        // If current pool is finished, shift to next
        if (!currentPool) {
            // End of round
            this.startNewRound();
            return;
        }

        // Select next available unit in current pool
        const nextUnit = currentPool.unitIds.find(id => id !== state.selectedUnitId) 
            || currentPool.unitIds[0];

        useBattleStore.setState({
            turnQueue: newActive,
            finishedPools: newFinished,
            selectedUnitId: nextUnit || null,
            selectedSkillId: null,
            inspectedUnitId: null,
        });
    }

    /**
     * Advance turn (used after queue is already updated)
     */
    private advanceTurn(
        newActive: TurnPool[],
        newFinished: TurnPool[]
    ): void {
        const currentPool = newActive[0];
        
        if (!currentPool) {
            this.startNewRound();
            return;
        }

        useBattleStore.setState({
            turnQueue: newActive,
            finishedPools: newFinished,
            selectedUnitId: currentPool.unitIds[0] || null,
            selectedSkillId: null,
            inspectedUnitId: null,
        });
    }

    /**
     * Start new round
     * Note: generateTurnQueue creates fresh pools without deferredUnitIds
     */
    private startNewRound(): void {
        const state = useBattleStore.getState();
        
        // Restore stamina
        const updatedUnits = new Map<string, BattleUnit>();
        const unitsForQueue: BattleUnit[] = [];

        for (const [id, unit] of state.units) {
            const updated = unit.currentStamina === unit.stats.stamina 
                ? unit 
                : { ...unit, currentStamina: unit.stats.stamina, opportunityAttackUsed: false };
            
            updatedUnits.set(id, updated);
            unitsForQueue.push(updated);
        }

        // Generate new queue (fresh pools, no deferredUnitIds)
        const newQueue = generateTurnQueue(unitsForQueue);
        const firstPool = newQueue[0];

        useBattleStore.setState({
            units: updatedUnits,
            turnQueue: newQueue,
            finishedPools: [], // Clear finished
            round: state.round + 1,
            selectedUnitId: firstPool?.unitIds[0] || null,
            selectedSkillId: null,
            inspectedUnitId: null,
        });
    }

    /**
     * Check if battle ended
     */
    private checkBattleEnd(): void {
        const units = useBattleStore.getState().units;
        
        let enemyCount = 0;
        let playerCount = 0;

        for (const unit of units.values()) {
            if (unit.isDead) continue;
            
            if (unit.faction === FactionType.Enemy) enemyCount++;
            else playerCount++;

            if (enemyCount > 0 && playerCount > 0) break;
        }

        if (enemyCount === 0) {
            useBattleStore.setState({
                status: BattleStatus.Victory,
                selectedUnitId: null,
                inspectedUnitId: null,
                selectedPath: initialPathInfo,
                reachableHexes: [],
                targetHexes: []
            });
        } else if (playerCount === 0) {
            useBattleStore.setState({
                status: BattleStatus.Defeat,
                selectedUnitId: null,
                selectedPath: initialPathInfo,
                inspectedUnitId: null,
                reachableHexes: [],
                targetHexes: []
            });
        }
    }
}